import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Carousel } from "antd";
import { getImageLink } from "../../services/asset";
import { getWindowWidth, isMobileScreen, parseVideoEmbedSrc } from "../../services/utils";
import { api } from "../../services";
import { selectTrackedCarouselAdIds, trackCarouselScroll } from "../../routes/Ad/ad.ducks";
import styles from "./indexCarousel.scss";
import Counter from "./Counter";
import { ImageSizeEnum } from "../../models";
import { Icon } from 'antd';
import ReactGA from "react-ga4";

interface indexCacouselInterface {
  className: string;
  images: string[];
  floorPlans?: string[];
  settings: settingsInterface;
  imageClassName: string;
  videoUrl: string | null;
  onClick: Function;
  showCounter: boolean;
  eventLabel?: string;
  navigationIcons?: React.ReactNode;
  showFullscreenIcon?: boolean;
  trackedAdId?: string;
}

interface settingsInterface {
  arrows?: boolean;
  autoplay?: boolean;
  dots?: boolean;
  fade?: boolean;
  infinite?: boolean;
  nextArrow?: any;
  prevArrow?: any;
  pauseOnHover?: boolean;
  slidesToScroll?: number;
  slidesToShow?: number;
}

const VideoThumbnailAlt = "Video thumbnail";
const FloorPlanAlt = "Floor plan";

// eslint-disable-next-line max-lines-per-function
function IndexCarousel(props: indexCacouselInterface) {
  const {
    className,
    images,
    floorPlans = [],
    settings,
    imageClassName,
    videoUrl,
    onClick,
    showCounter,
    eventLabel,
    navigationIcons,
    showFullscreenIcon,
    trackedAdId
  } = props;

  const isMobile = isMobileScreen(getWindowWidth());
  const numOfPreviewing = images.length + floorPlans.length + (videoUrl ? 1 : 0);
  const [imagesSrc, setImagesSrc] = useState([]);
  const dispatch = useDispatch();
  const trackedCarouselAdIds = useSelector(selectTrackedCarouselAdIds);

  // Track drag state to differentiate clicks from swipes
  const dragState = useRef({ startX: 0, startY: 0, isDragging: false });
  const DRAG_THRESHOLD = 10; // pixels

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    const point = 'touches' in e ? e.touches[0] : e;
    dragState.current = { startX: point.clientX, startY: point.clientY, isDragging: false };
  };

  const handlePointerUp = (e: React.MouseEvent | React.TouchEvent, index: number) => {
    const point = 'changedTouches' in e ? e.changedTouches[0] : e;
    const deltaX = Math.abs(point.clientX - dragState.current.startX);
    const deltaY = Math.abs(point.clientY - dragState.current.startY);

    // Only trigger click if movement was below threshold (it was a tap, not a swipe)
    if (deltaX < DRAG_THRESHOLD && deltaY < DRAG_THRESHOLD) {
      onClick(e, index);
    }
  };

  useEffect(() => {
    // Add regular images
    const tempImagesSrc = images.map((imageName: string) => ({
      src: getImageLink(imageName, isMobile ? ImageSizeEnum.cardMedium : ImageSizeEnum.cardLarge),
      alt: "Slike oglasa"
    }));

    // Add floor plan images
    floorPlans.forEach((floorPlanName: string) => {
      tempImagesSrc.push({
        src: getImageLink(floorPlanName, isMobile ? ImageSizeEnum.cardMedium : ImageSizeEnum.cardLarge),
        alt: FloorPlanAlt
      });
    });

    if (!videoUrl) {
      setImagesSrc(tempImagesSrc);
    } else {
      parseVideoEmbedSrc(videoUrl).then(videoEmbedSrc => {
        if (videoEmbedSrc) {
          tempImagesSrc.push({ src: videoEmbedSrc.thumbnail, alt: VideoThumbnailAlt });
          setImagesSrc(tempImagesSrc);
        }
      });
    }
  }, [images, floorPlans, videoUrl]);

  const renderImage = (image: { src: string; alt: string }, index: number) => (
    <React.Fragment>
      {image.alt === VideoThumbnailAlt ? (
        //TODO: Davor - maybe replace with unified video icon, or make handle for video service
        <svg className={styles.playIcon} viewBox="0 0 68 48">
          <path
            d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
            fill="#f00"
            fillOpacity="0.8"
          />
          <path d="M 45,24 27,14 27,34" fill="#fff" />
        </svg>
      ) : null}
      <div
        className={`${imageClassName} ${styles.carouselImage}`}
        // @ts-ignore
        alt={"Slike oglasa"}
        style={{ backgroundImage: `url(${image.src})`, position: "relative" }}
      >
        {showFullscreenIcon && (<div
          className={styles.fullscreenIcon}
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onClick(e, index); }}
        >
          <Icon type="fullscreen" />
        </div>)}
        {(showCounter || navigationIcons) && (
          <div className={styles.bottomControls}>
            {showCounter && (
              <Counter total={numOfPreviewing} currentIndex={index + 1} />
            )}
            {navigationIcons}
          </div>
        )}
      </div>
    </React.Fragment>
  );

  return (
    <Carousel
      className={`child-center adcard-carousel-image ${className}`}
      {...settings}
      afterChange={e => {
        if (eventLabel !== "single") {
          ReactGA.event({
            category: "Slider interaction",
            action: "Click",
            label: eventLabel || `Ad List - gallery swipe`,
          });
        }

        if (trackedAdId && (!trackedCarouselAdIds || trackedCarouselAdIds.indexOf(trackedAdId) === -1)) {
          dispatch(trackCarouselScroll(trackedAdId));
          api.Ads.carouselScroll(trackedAdId);
        }
      }}
    >
      {imagesSrc.length > 0 ? (
        imagesSrc.map((image: { src: string; alt: string }, index: number) => (
          <div
            // eslint-disable-next-line
            key={index}
            role="presentation"
            onMouseDown={handlePointerDown}
            onMouseUp={e => handlePointerUp(e, index)}
            onTouchStart={handlePointerDown}
            onTouchEnd={e => handlePointerUp(e, index)}
          >
            {renderImage(image, index)}
          </div>
        ))
      ) : (
        <img
          alt={"Prekriti"}
          className={styles.carouselImage}
          src={getImageLink()}
        />
      )}
    </Carousel>
  );
}

IndexCarousel.defaultProps = {
  settings: {
    autoplay: false,
    arrows: true,
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnHover: true,
    nextArrow: <Icon type="right" />,
    prevArrow: <Icon type="left" />,
    lazyLoad: 'progressive',
  },
  images: [],
  floorPlans: [],
  imageClassName: "",
  videoUrl: "",
  className: "",
  onClick: () => { },
  showCounter: false,
  navigationIcons: null
};

export default IndexCarousel;



// WEBPACK FOOTER //
// ./src/components/IndexCarousel/IndexCarousel.tsx